import React, { useState } from 'react';
import { sheetsClient } from '../../data/sheetsClient';
import { useTeacherData } from '../../context/TeacherDataContext';
import { Card, Button, Skeleton } from '../shared/UI';

const linkStyle = { fontSize: 14, color: '#2D2E33', fontWeight: 500 };

export default function AccommodationsMeals({ navigateTo }) {
  const { timeline, loading, refetch } = useTeacherData();
  const [saving, setSaving] = useState(null); // null | 'request' | 'payment' | 'opt-out'
  const [error, setError] = useState('');

  if (loading.timeline) {
    return (
      <div style={{ padding: 20, maxWidth: 680, margin: '0 auto' }}>
        <Skeleton width={220} height={26} style={{ marginBottom: 10 }} />
        <Skeleton width={340} height={14} style={{ marginBottom: 20 }} />
        <Card><Skeleton height={60} /></Card>
      </div>
    );
  }

  const step = (timeline || []).find((s) => s.title === 'Accommodations & Meals Request');
  const status = step?.accommodationStatus || '';
  const requestComplete = step?.accommodationRequestComplete || false;
  const paymentComplete = step?.accommodationPaymentComplete || false;
  const optedOut = status === 'Opted Out';
  // Pulled from columns D and E of the Timeline sheet's "Accommodations &
  // Meals Request" row, rather than hardcoded here.
  const infoDocUrl = step?.accommodationInfoDocUrl || '';
  const mealFormUrl = step?.accommodationMealFormUrl || '';

  const setStatus = async (next, key) => {
    setSaving(key);
    setError('');
    try {
      await sheetsClient.setAccommodationStatus(next);
      await refetch.timeline();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  const toggleRequest = () => setStatus(requestComplete ? '' : 'Request Complete', 'request');
  // Checking payment always implies the request step is done too; unchecking
  // it only rolls back to "request complete", not all the way to blank.
  const togglePayment = () => setStatus(paymentComplete ? 'Request Complete' : 'Payment Complete', 'payment');
  const optOut = () => setStatus('Opted Out', 'opt-out');
  const undoOptOut = () => setStatus('', 'opt-out');

  return (
    <div style={{ padding: 20, maxWidth: 680, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <Button size="sm" onClick={() => navigateTo('dashboard')}>← Back to dashboard</Button>
      </div>

      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Accommodations & Meals</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
        Some schools need on-site accommodations and meals arranged for their group — others don't.
        Use the info below to figure out which applies to you.
      </p>

      <Card title="Accommodation & Meals info">
        <p style={{ fontSize: 14, color: '#444', marginBottom: 10 }}>
          Full details on what's available, pricing, and how it works are in this document:
        </p>
        {infoDocUrl ? (
          <a href={infoDocUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
            📄 Accommodation & Meals Info ↗
          </a>
        ) : (
          <span style={{ fontSize: 13, color: '#999' }}>Link not available yet — check back soon.</span>
        )}
      </Card>

      <Card title="Meal request form">
        <p style={{ fontSize: 14, color: '#444', marginBottom: 10 }}>
          If your school needs meals arranged, complete this form and email it directly to the venue
          — it isn't submitted through this portal.
        </p>
        {mealFormUrl ? (
          <a href={mealFormUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
            📄 Meal Request Form ↗
          </a>
        ) : (
          <span style={{ fontSize: 13, color: '#999' }}>Link not available yet — check back soon.</span>
        )}
      </Card>

      <Card title="Status">
        {optedOut ? (
          <div>
            <p style={{ fontSize: 14, color: '#444', marginBottom: 10 }}>
              Your school has opted out of accommodations & meals.
            </p>
            <Button size="sm" onClick={undoOptOut} disabled={saving === 'opt-out'}>
              {saving === 'opt-out' ? 'Undoing…' : 'Undo opt out'}
            </Button>
          </div>
        ) : (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 10, cursor: paymentComplete ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={requestComplete} disabled={paymentComplete || saving === 'request'} onChange={toggleRequest} />
              I've completed and emailed the accommodations & meals request form
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={paymentComplete} disabled={saving === 'payment'} onChange={togglePayment} />
              I've completed payment for accommodations & meals
            </label>
            {error && <div style={{ fontSize: 12, color: '#C0392B', marginBottom: 10 }}>{error}</div>}
            <div style={{ borderTop: '1px solid #E5E5E5', paddingTop: 14 }}>
              <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                Don't need accommodations or meals for your school this year?
              </p>
              <Button variant="danger" size="sm" onClick={optOut} disabled={saving === 'opt-out'}>
                {saving === 'opt-out' ? 'Saving…' : 'Opt out of accommodations & meals'}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
